/**
 * Block (INSERT) extents for the entity metadata index — the real
 * transformed footprint of the referenced block definition, memoized per
 * `blocks` table.
 *
 * WHY: `entityMetadataIndex.ts::boundsForEntity` used to anchor a `Block`
 * (INSERT) on its insertion point only (a degenerate 0×0 box). On the
 * cache-direct render path (streaming import, DXF ≥ 2 MB: `spaces[].entities`
 * empty, only the compact cache + metadata index populated) the spatial stub
 * of EVERY INSERT therefore had a 0 px screen size and was dropped by the LOD
 * pre-classify (`"skip"`), so no INSERT rendered — ~92 % of the geometry of a
 * block-heavy drawing (audit `docs/architecture/2026-09-05_acdb-comparison-
 * and-delivery-audit.md` §11.2). AutoCAD's own INSERT extents (the basis of
 * ZOOM E, selection, culling) are the definition's geometry under the INSERT
 * transform — this module projects exactly that into the index.
 *
 * WHAT: the bounds math is NOT re-derived here — `@dxf-ai-editor/cad-core`'s
 * `getBlockInsertBoundsOrNull` (`blockGeometry.ts`) already resolves the
 * definition's per-kind child geometry recursively (nested INSERTs with their
 * own transform + MINSERT grid, cycle-safe) under the INSERT affine map
 * (insertion point, `scaleX`/`scaleY` incl. negative/mirror, `rotation`,
 * definition `basePoint`, MINSERT `columnCount`/`rowCount`/spacing). This
 * module only adds the CACHE, the DYNAMIC-block evaluation hook
 * ({@link resolveDynamicBlockChildren} — per-instance evaluated children,
 * the same `evaluateBlock` entry point the typed renderer uses) and the
 * index-facing fallback contract.
 *
 * CACHE CONTRACT — keyed by (`blocks` table IDENTITY, DIMSTYLE table IDENTITY):
 *   `cad-core` memoizes a definition's block-LOCAL bounds in a
 *   `BlockDefinitionBoundsMemo` (`BlockLookupContext.definitionBoundsMemo`).
 *   One memo is held per (`Record<string, BlockDefinition>` OBJECT,
 *   `DimensionStyleTable` OBJECT) PAIR.
 *
 *   The `blocks` half is correct because every reducer that changes a block
 *   definition replaces `document.blocks` IMMUTABLY with a new object
 *   (`blockEditReducer.ts::syncBlockEditSpaceToDefinition` →
 *   `blocks: { ...document.blocks, [blockName]: nextBlockDef }`;
 *   `attributeReducerHelpers.ts` `blocks: { ...document.blocks, ... }`;
 *   `blockReducer.ts` create/redefine) — so a NEW `blocks` identity IS the
 *   invalidation signal, and the SAME identity guarantees every definition
 *   (and the whole nested-INSERT graph) is unchanged. In-place mutation of a
 *   `BlockDefinition` inside an existing `blocks` object is a violation of
 *   the state contract (CLAUDE.md ПРИОРИТЕТ #2.1, skill
 *   `state-mutation-integrity`) and would go undetected here — the same
 *   assumption every other derived index in this directory makes. Callers
 *   holding such a table for a test/bootstrap reason can drop its memos
 *   explicitly via {@link invalidateBlockExtentsCache}.
 *
 *   The DIMSTYLE half (Codex PR #784 round 4 P1) is REQUIRED, not an
 *   optimisation: a memoized definition box that contains a `Dimension` child
 *   depends on that child's NAMED style DIMVARs (DIMEXE / DIMEXO / DIMTXT /
 *   DIMGAP / DIMASZ — `cad-core/leafBounds.ts::dimensionBoundsPoints`), and
 *   editing a named DIMSTYLE replaces `styleTables.dimensionStyles` while
 *   leaving `blocks` IDENTICAL. Keyed by `blocks` alone, the memo would serve
 *   the OLD style's box against the NEW table — enlarged dimension geometry
 *   culled or missed by broad-phase selection. cad-core states the same rule
 *   from its side (`blockDefinitionWalk.ts::DefinitionWalk.dimensionStyles`:
 *   the style table is not part of the memo key, so a caller resolving one
 *   `blocks` object against two different style tables must pass two memos) —
 *   this module is that caller, and this is where the pair key lives.
 *
 *   EVICTION: outer `WeakMap` keyed by the `blocks` object, inner `WeakMap`
 *   keyed by the `DimensionStyleTable` object (plus one strong slot for the
 *   "no style table supplied" resolution, held by the outer entry). A memo is
 *   reachable only while BOTH its `blocks` table and its style table are
 *   alive, and both are document-owned objects replaced immutably on every
 *   edit — so a replaced/closed document's tables are collected together with
 *   every memo derived from them, and a session of N successive DIMSTYLE
 *   edits leaves N-1 unreachable (collectable) inner entries rather than an
 *   unbounded map. No manual eviction is needed on the mutation contour.
 *
 * FALLBACK CONTRACT: the index must always hold SOME finite box for an INSERT
 * (the spatial index / viewport cull / selectors skip nothing silently). When
 * `blocks` is `undefined`, the referenced definition is missing, has no
 * usable child geometry, is cyclic, or resolves to a non-finite box, the
 * bounds degrade to the previous behaviour — the insertion-point degenerate
 * box (an honest "extent unknown beyond the placement point", never a
 * fabricated icon-sized box), or the zero box when even the insertion point
 * is non-finite (mirrors `entityMetadataIndex.ts::fromPoints`).
 */

import {
  buildEvaluateBlockCacheKey,
  evaluateBlock,
} from "@dxf-ai-editor/block-evaluator";
import {
  getBlockInsertBoundsOrNull,
  type BlockDefinitionBoundsMemo,
  type DimensionStyleTable,
  type DynamicChildrenResolver,
  type EntityBounds,
} from "@dxf-ai-editor/cad-core";
import type {
  BlockDefinition,
  BlockInsertEntity,
  DimensionStyle,
} from "@dxf-ai-editor/drawing-model";

/** `DrawingDocument.blocks` — the block table a memo is keyed by. */
export type BlockTable = Record<string, BlockDefinition>;

/**
 * The memos held for ONE `blocks` object: one per DIMSTYLE table identity
 * resolved against it, plus the `undefined`-table slot (see the module doc's
 * cache contract). The inner `WeakMap` is what keeps the pair key bounded —
 * a style table dropped by a later edit takes its memo with it.
 */
interface BlockMemoEntry {
  /** Memo of the resolutions run WITHOUT a DIMSTYLE table (cad-core defaults). */
  withoutStyles?: BlockDefinitionBoundsMemo;
  /** Memo per `DimensionStyleTable` object resolved against these blocks. */
  readonly byStyleTable: WeakMap<DimensionStyleTable, BlockDefinitionBoundsMemo>;
}

const memoByBlocks = new WeakMap<BlockTable, BlockMemoEntry>();

/**
 * Name-keyed DIMSTYLE tables, memoized by the identity of the
 * `styleTables.dimensionStyles` ARRAY they were built from — the same
 * "immutably replaced ⇒ new identity ⇒ fresh derivation" contract the block
 * memo above documents (every reducer that edits a DIMSTYLE replaces the
 * array). Bounded by the `WeakMap`: a replaced table is collected with its
 * document.
 */
const dimStyleTableByArray = new WeakMap<
  readonly DimensionStyle[],
  DimensionStyleTable
>();

/**
 * The document's DIMSTYLE table in the name-keyed shape cad-core's
 * `BlockLookupContext.dimensionStyles` takes (Codex PR #784 round 3 P1) —
 * built once per `styleTables.dimensionStyles` array identity and reused for
 * every INSERT resolved against it, mirroring
 * {@link blockDefinitionBoundsMemoFor}'s own per-identity discipline (an
 * index rebuild walks every INSERT of a drawing; re-keying the table per
 * entity would be O(styles) per INSERT).
 *
 * `Object.create(null)`, never a `{}` literal — a DXF DIMSTYLE genuinely named
 * `"__proto__"` assigned onto a plain object literal invokes the
 * `Object.prototype.__proto__` SETTER instead of creating an own property, so
 * the style would silently VANISH from the table rather than being resolvable
 * by `safeRecordLookup`. Identical rule and rationale to
 * `captureSnapshot.ts`'s own build of this same table (@codex T1 arm(c)
 * round-25 Fix 10) — the WRITE-side twin of `safeRecordLookup`'s READ guard.
 *
 * `undefined` in ⇒ `undefined` out: cad-core then bounds a Dimension with
 * AutoCAD's hardcoded DIMVAR defaults, the pre-fix behaviour.
 */
export function dimensionStyleTableFor(
  styles: readonly DimensionStyle[] | undefined,
): DimensionStyleTable | undefined {
  if (styles === undefined) return undefined;
  const cached = dimStyleTableByArray.get(styles);
  if (cached !== undefined) return cached;
  const table = Object.create(null) as Record<string, DimensionStyle>;
  for (const style of styles) table[style.name] = style;
  dimStyleTableByArray.set(styles, table);
  return table;
}

/**
 * The cad-core definition-bounds memo bound to this exact
 * (`blocks`, `dimensionStyles`) PAIR — created on first use, reused for every
 * later INSERT resolved against the SAME pair (see the module doc's cache
 * contract). Two different DIMSTYLE tables over one `blocks` object get two
 * memos, so a style edit can never be served a box computed under the old
 * style; `undefined` (no table ⇒ cad-core's hardcoded DIMVAR defaults) is its
 * own third key, not an alias of any table.
 */
export function blockDefinitionBoundsMemoFor(
  blocks: BlockTable,
  dimensionStyles?: DimensionStyleTable,
): BlockDefinitionBoundsMemo {
  let entry = memoByBlocks.get(blocks);
  if (entry === undefined) {
    entry = { byStyleTable: new WeakMap() };
    memoByBlocks.set(blocks, entry);
  }
  if (dimensionStyles === undefined) {
    entry.withoutStyles ??= new Map();
    return entry.withoutStyles;
  }
  let memo = entry.byStyleTable.get(dimensionStyles);
  if (memo === undefined) {
    memo = new Map();
    entry.byStyleTable.set(dimensionStyles, memo);
  }
  return memo;
}

/**
 * Drop EVERY memo held for `blocks` (all DIMSTYLE-table variants at once).
 * Not needed on the production mutation contour (reducers replace the table →
 * new identity → fresh memo); exposed for tests and for any bootstrap/import
 * code that legitimately fills a `blocks` object in place before it becomes
 * document state.
 */
export function invalidateBlockExtentsCache(blocks: BlockTable): void {
  memoByBlocks.delete(blocks);
}

/**
 * True when at least one memo currently exists for this exact `blocks` object
 * (any DIMSTYLE-table variant, the `undefined` one included).
 */
export function hasBlockExtentsCacheEntry(blocks: BlockTable): boolean {
  return memoByBlocks.has(blocks);
}

/**
 * DYNAMIC-block evaluation hook for cad-core (`BlockLookupContext.
 * resolveDynamicChildren` — Codex PR #784 round 3 P1 "evaluate per-instance
 * state before indexing bounds"). A definition with `dynamicMetadata`
 * renders the EVALUATED children of each INSERT's own `customProperties` /
 * `visibilityState` (move / stretch / polar / array actions, visibility
 * states) — so its index bounds must be resolved from that same evaluated
 * list, not the authored `def.entities` (which both includes geometry an
 * action relocates AND misses where the action moved it to).
 *
 * ONE TRUTH — this is the SAME entry point, called the SAME way, the typed
 * renderer uses (`scene3d/adapters/typed/blockFromEntity.ts::
 * resolveEvaluatedChildren`): the static short-circuit on
 * `!def.dynamicMetadata` (zero evaluator cost for the common case; the
 * instance-agnostic memo path stays untouched), the instance built from
 * `customProperties ?? {}` + `visibilityState` (only when defined), the
 * `evaluateBlock({ blockDef, instance, blockDefinitions, parentChain })`
 * call with the ancestor definition-name chain for the evaluator's own
 * nested-depth / cycle guard, and the `buildEvaluateBlockCacheKey` memo key.
 * cad-core memoizes the evaluated-definition bounds per (memo, ancestor
 * chain, instanceKey) — the renderer's own `[parentChain, key]` keying, so
 * an evaluation the evaluator's depth guard left un-evaluated never serves a
 * shallower INSERT — and the `children` thunk runs once per distinct
 * (chain, state) per `blocks` table; `evaluateBlock` itself never runs for a
 * static definition. cad-core retains the evaluated list per entry so an
 * INSERT's ATTRIB rows resolve against the EVALUATED ATTDEF anchors
 * (`mapTextAnchors` moves them with the geometry), as the renderer places
 * them.
 *
 * Nested dynamic children are handled by cad-core re-invoking this hook per
 * nesting level with the CHILD INSERT's own state — mirroring how the
 * renderer re-invokes `evaluateBlock` per level (the evaluator does not
 * recurse into nested INSERTs by itself). The anonymous `*U<N>` variant gap
 * (`resolveEvaluatedChildren`'s doc) is shared by construction: such a
 * variant has no `dynamicMetadata` and stays static here too.
 */
export const resolveDynamicBlockChildren: DynamicChildrenResolver = (
  insert,
  def,
  blocks,
) => {
  if (!def.dynamicMetadata) return undefined;
  const customProperties = insert.customProperties ?? {};
  const instance =
    insert.visibilityState !== undefined
      ? { customProperties, visibilityState: insert.visibilityState }
      : { customProperties };
  return {
    instanceKey: buildEvaluateBlockCacheKey(def.name, instance),
    children: (parentChain) =>
      evaluateBlock({
        blockDef: def,
        instance,
        blockDefinitions: blocks,
        parentChain,
      }).entities as BlockDefinition["entities"],
  };
};

function isFiniteBounds(b: EntityBounds): boolean {
  return (
    Number.isFinite(b.minX) &&
    Number.isFinite(b.minY) &&
    Number.isFinite(b.maxX) &&
    Number.isFinite(b.maxY)
  );
}

/**
 * The pre-existing index bounds of an INSERT — its insertion point as a
 * degenerate box (`fromPoints([entity.insertionPoint])`), or the zero box
 * when the point is non-finite.
 */
export function blockInsertionPointBounds(
  entity: BlockInsertEntity,
): EntityBounds {
  const { x, y } = entity.insertionPoint;
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }
  return { minX: x, minY: y, maxX: x, maxY: y };
}

/**
 * Index bounds of a `Block` (INSERT) entity: the referenced definition's
 * child-geometry AABB under the INSERT transform (nested INSERTs, MINSERT
 * grid included), resolved through cad-core with the per-`blocks` memo; the
 * insertion-point fallback (see module doc) when it cannot be resolved.
 *
 * `dimensionStyles` (Codex PR #784 round 3 P1) — the document's name-keyed
 * DIMSTYLE table ({@link dimensionStyleTableFor} over
 * `document.styleTables.dimensionStyles`). A definition containing a
 * `Dimension` child is bounded with the DIMVARs of that child's NAMED style
 * (DIMEXE / DIMEXO / DIMTXT / DIMGAP / DIMASZ), the same row the renderer
 * resolves (`scene3d/adapters/typed/dimensionFromEntity.ts` →
 * `ctx.dimensionStyles`); without it the walk fell to AutoCAD's hardcoded
 * defaults and the rendered dimension overflowed the indexed box, so
 * cache-direct culling, fit-to-extents and broad-phase selection dropped it.
 * Omitted ⇒ the pre-fix default-based box (never a hard requirement — the
 * same "absent context degrades honestly" contract `blocks` itself has).
 * It is part of the MEMO KEY (round 4 P1): the same `blocks` object resolved
 * against a NEW style table gets a fresh memo, so a named-DIMSTYLE edit —
 * which replaces `styleTables.dimensionStyles` but leaves `blocks` identical —
 * cannot be served the box computed under the old style.
 *
 * Returns a NEW object every call — callers may store it directly as
 * `EntityMetadata.bounds` without aliasing the memoized definition box.
 */
export function blockInsertMetadataBounds(
  entity: BlockInsertEntity,
  blocks: BlockTable | undefined,
  dimensionStyles?: DimensionStyleTable,
): EntityBounds {
  if (blocks === undefined) return blockInsertionPointBounds(entity);
  const resolved = getBlockInsertBoundsOrNull(entity, {
    blockDefinitions: blocks,
    definitionBoundsMemo: blockDefinitionBoundsMemoFor(blocks, dimensionStyles),
    resolveDynamicChildren: resolveDynamicBlockChildren,
    ...(dimensionStyles === undefined ? {} : { dimensionStyles }),
  });
  if (resolved === null || !isFiniteBounds(resolved)) {
    return blockInsertionPointBounds(entity);
  }
  return {
    minX: resolved.minX,
    minY: resolved.minY,
    maxX: resolved.maxX,
    maxY: resolved.maxY,
  };
}
